import { CSSProperties } from "react";

import { PITCH_SLIDER_MIN, PITCH_SLIDER_RANGE } from "@/constants/audioQuality";
import { BADGE_COLOR } from "@/constants/badgeColor";
import { PitchRecommendation } from "@/types/defs/audioQuality/pitchRecommendation";

const buildPitchWindowGradient = (
  sweetSpotPercent: number,
  leftEdgePercent: number,
  rightEdgePercent: number
): string => {
  const clampedSweetSpot = Math.max(0, Math.min(100, sweetSpotPercent));
  const clampedLeft = Math.max(0, Math.min(clampedSweetSpot, leftEdgePercent));
  const clampedRight = Math.max(
    clampedSweetSpot,
    Math.min(100, rightEdgePercent)
  );
  const leftMidpoint = (clampedLeft + clampedSweetSpot) / 2;
  const rightMidpoint = (clampedSweetSpot + clampedRight) / 2;
  return [
    `${BADGE_COLOR.grayNeutral} 0%`,
    `${BADGE_COLOR.grayNeutral} ${clampedLeft}%`,
    `${BADGE_COLOR.greenLight} ${leftMidpoint}%`,
    `${BADGE_COLOR.green} ${clampedSweetSpot}%`,
    `${BADGE_COLOR.greenLight} ${rightMidpoint}%`,
    `${BADGE_COLOR.grayNeutral} ${clampedRight}%`,
    `${BADGE_COLOR.grayNeutral} 100%`,
  ].join(", ");
};

// Builds the pitch-window highlight for the pitch shift slider — the colored sweet-spot gradient on the track plus the hover tooltip wired to the highlighted region. Returns null when there is no recommendation to show.
export const getPitchWindowSliderHighlight = (
  pitchRecommendation: PitchRecommendation
): {
  trackStyle: CSSProperties;
  hoverTooltip: {
    content: string;
    activeStartPercent: number;
    activeEndPercent: number;
  };
} | null => {
  if (pitchRecommendation === null) {
    return null;
  }
  const { recommendedPitch, pitchShiftWindow } = pitchRecommendation;

  const sweetSpotPercent =
    ((recommendedPitch - PITCH_SLIDER_MIN) / PITCH_SLIDER_RANGE) * 100;
  const leftEdgePercent =
    ((pitchShiftWindow.min - PITCH_SLIDER_MIN) / PITCH_SLIDER_RANGE) * 100;
  const rightEdgePercent =
    ((pitchShiftWindow.max - PITCH_SLIDER_MIN) / PITCH_SLIDER_RANGE) * 100;

  return {
    trackStyle: {
      background: `linear-gradient(to right, ${buildPitchWindowGradient(
        sweetSpotPercent,
        leftEdgePercent,
        rightEdgePercent
      )})`,
    },
    hoverTooltip: {
      content: "This shows the ideal pitch range for this voice",
      activeStartPercent: leftEdgePercent,
      activeEndPercent: rightEdgePercent,
    },
  };
};
